// src/components/MobileAnalyticsTable.jsx
import React from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import {
  filterAnalyticsRows,
  getNewTrancheColumns,
  hasOnlyNewTranches,
  NEW_CUSIP_NOTICE,
  NEW_TRANCHE_LABEL,
  orderAnalyticsRows,
} from '../utils/analyticsRows';

const MobileAnalyticsTable = ({ data }) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No analytics data available
        </Typography>
      </Box>
    );
  }

  // Every column but the metric name is a tranche; one with no book value is a
  // CUSIP we do not hold, and its position rows come out of the table.
  const trancheColumns = Object.keys(data[0] || {}).filter((column) => column !== 'Metric');
  const newColumns = getNewTrancheColumns(data, { columns: trancheColumns });
  const rows = orderAnalyticsRows(filterAnalyticsRows(data, { columns: trancheColumns }));

  const notice = hasOnlyNewTranches(data, { columns: trancheColumns }) ? (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      {NEW_CUSIP_NOTICE}
    </Typography>
  ) : null;

  if (rows.length === 0) {
    return (
      <Box>
        {notice}
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No analytics data available
          </Typography>
        </Box>
      </Box>
    );
  }

  // Get all column names from the first row
  const columns = Object.keys(rows[0]);

  // Format cell value
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'number') {
      return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
    }
    return String(value);
  };

  return (
    <Box>
      {notice}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '60vh',
          overflowX: 'auto',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column}
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#f8fafc',
                    color: theme.palette.mode === 'dark' ? 'white' : '#1e293b',
                    fontWeight: 600,
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    py: 2,
                    px: 2,
                    borderBottom: `2px solid ${theme.palette.divider}`
                  }}
                >
                  {column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {newColumns.has(column) && (
                    <Chip
                      label={NEW_TRANCHE_LABEL}
                      size="small"
                      sx={{ ml: 1, height: 18, fontSize: '11px', fontWeight: 600 }}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  '&:nth-of-type(odd)': {
                    bgcolor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.5)
                      : alpha(theme.palette.primary.main, 0.02)
                  },
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.primary.main, 0.05)
                  }
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      fontSize: '14px',
                      py: 2,
                      px: 2,
                      whiteSpace: 'nowrap',
                      color: theme.palette.text.primary
                    }}
                  >
                    {formatValue(row[column])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MobileAnalyticsTable;

